'use client'

import type { WelcomeCoupon } from '@/lib/marketing/welcomeCouponWire'
import { useEffect, useMemo, useRef, useState, type ComponentProps, type ReactNode } from 'react'
import { motion } from 'framer-motion'
import { UtensilsCrossed, ShoppingBag } from 'lucide-react'
import { useLocale, useTranslations } from 'next-intl'
import { effectiveCashDiscountPct, type PricingConfig } from '@/lib/utils/pricing'
import { donationCharge } from '@/lib/donations/donationCharge'
import type { CartItem, CustomerInfo, DeliveryMode, DropoffAddress, HeldQuote, ShipQuote, ShipToAddress, SoldOutNotice } from '../types'
import type { StorefrontSection } from '@/lib/microsite/storefrontKind'
import type { MicrositeCartMenuItem } from '../RestaurantMicrosite'
import { normalizePhone } from '@/lib/wallet/identity'
import type { SubstitutionPref, SubstitutionWarning } from '@/lib/online-orders/substitutionPrefs'
import { receiptCodeFromTrackingUrl } from '@/lib/orderTracking/receiptCodeFromUrl'
import type { OpenStatus } from '@/lib/hours/openStatus'
import type { ScheduleDay } from '@/lib/hours/scheduleSlots'
import type { ScheduleMode } from '../SchedulePicker'
import { AddressStep } from './AddressStep'
import { ShipAddressStep } from './ShipAddressStep'
import { CartStep } from './CartStep'
import { CustomerStep } from './CustomerStep'
import { SubstitutionsStep } from './SubstitutionsStep'
import { shouldHandOffPayment, hostedPaymentUrl, saveHandoffStash } from '@/lib/microsite/hostedPayment'
import { PaymentStep } from './PaymentStep'
import type { TableContext } from './tableContext'
import { qrTableFields } from './qrTableFields'
import { ConfirmStep } from './ConfirmStep'
import { DonationStep } from './DonationStep'
import { DriverTipStep } from './DriverTipStep'
import { StepProgress } from './StepProgress'
import { useScrollLock } from '../SheetPortal'
import type { ActiveCampaign } from "@/lib/donations/getActiveCampaign"
import { buildTableCustomer } from '@/lib/qr/tableCustomer'
import { loadCheckoutScript } from '@/lib/north/checkoutScript'
import { shipModeAvailable } from './shipGate'
import { buildSteps, buildStepsV2, nextStep, prevStep, NO_SKIP, type Step } from './stepMachine'
import { deliveryDisabledWhenClosed, gateV2, skipSetV2, whenNeeded } from '@/lib/microsite/checkoutV2'
import { CheckoutHeaderV2, type BreakdownLine } from './CheckoutHeaderV2'
import { funnelStepOf, makeCheckoutFunnel, type CheckoutFunnel, type FunnelCtx } from '@/lib/microsite/checkoutFunnel'
import { isQuoteExpired, QUOTE_CHECKOUT_MARGIN_MS } from '@/lib/delivery/dispatchable'
import { requestDeliveryQuote } from './requestDeliveryQuote'
import { requestCartAvailability } from './requestCartAvailability'
import { applyCartRecheck, planCartRecheck } from '@/lib/microsite/cartAvailability'
import type { CartLineFacts } from '@/lib/storefront/groceryCart'
import { retailStockItemIds, useRetailStock } from '../useRetailStock'
import { shouldRequoteOnOpen } from './requoteDecision'
import {
  couponDisplayState,
  micrositeCartSubtotal,
  micrositeCouponBase,
  micrositeOrderTotals,
  micrositeTaxableSubtotal,
  type AppliedCoupon,
  type SessionCouponRejection,
} from '@/lib/microsite/couponUx'
import {
  micrositeVisitRewardDiscount,
  selectCoupon,
  selectVisitReward,
  type AppliedVisitReward,
  type VisitRewardNotice,
} from '@/lib/microsite/visitRewardUx'
import {
  shipCartFingerprint,
  shipQuoteInvalidatedByCartChange,
  shipTaxMustBeZero,
  shipModeShouldDegradeToPickup,
} from '@/lib/microsite/shippingCheckout'
import { formatDropoffLine, formatDropoffFull } from '@/lib/delivery/formatDropoff'
import type { WaitEstimate } from '@/lib/microsite/waitEstimate'
import { cartValue, track, trackItemsFromCart, trackPurchase } from '@/lib/tracking/storefrontEvents'
import type { Conversion } from '@/lib/tracking/conversionValue'

type Props = {
  location: {
    id: string
    name: string
    /** Slug del local: los pasos del checkout lo necesitan para enlazar la
        política de devoluciones DEL MERCHANT (`/order/{slug}/refund-policy`). */
    slug: string
    tax_rate: number | null
    cash_discount_pct: number | null
    cash_per_transaction_fee?: number | null
    processing_fee_enabled?: boolean | null
    /** Precios con impuesto incluido: el total no suma impuesto y la fila dice
     *  "incluido". Ausente = impuesto encima (mismo default que el servidor). */
    prices_include_tax?: boolean | null
    timezone?: string | null
    /** Estado del local (G4/tax_mismatch): sin él, un envío se trata SIEMPRE
     *  como fuera de estado (fail-closed) — ver shipTaxMustBeZero. */
    state?: string | null
  }
  cart: CartItem[]
  setCart: (cart: CartItem[]) => void
  /** Config de precios del local (redondeo de tarjeta para el DISPLAY del carrito). */
  pricingCfg: PricingConfig
  accentColor: string
  pickupWaitMinutes?: number
  /** Tiempo de espera que muestra la tienda antes de pagar (PR K); ver `lib/microsite/waitEstimate.ts`. */
  waitEstimate?: WaitEstimate
  /** El que se muestra en la confirmación. Con `live_wait_time: "confirmation"`
   *  es el real aunque la tienda muestre el fijo. Ausente = `waitEstimate`. */
  confirmWaitEstimate?: WaitEstimate
  mapsUrl?: string
  /** false => el local no toma pedidos ahora (cerrado o last-call); bloquea el avance a pago. */
  acceptingOrders?: boolean
  openStatus?: OpenStatus | null
  /** Botón de pánico: ventas online pausadas (cierre de emergencia / sold out). Bloquea ASAP y agendado. */
  orderingPaused?: boolean
  /** Instante en que la pausa se auto-levanta (para el aviso), o null si es manual. */
  pauseResumesAt?: string | null
  /** Agendado habilitado para este local. */
  schedulingEnabled?: boolean
  /** Slots disponibles para agendar (vacío si no aplica). */
  scheduleDays?: ScheduleDay[]
  onClose: () => void
  activeDonationCampaign?: ActiveCampaign | null
  donationEnabled?: boolean
  /** Modo mesa (pedido por QR): presente ⇒ sin pickup/agendado, POST con qr_token. */
  tableContext?: TableContext
  // ─── Delivery (Fase 4) ────────────────────────────────────────────────────
  // El estado vive en RestaurantMicrosite porque este sheet se DESMONTA al
  // cerrarse (AnimatePresence): si viviera acá, el cliente perdería la dirección
  // y la cotización al volver al menú por otro platillo.
  /** Delivery propio de Redy habilitado para el local; undefined/false ⇒ solo pickup. */
  deliveryEnabled?: boolean
  deliveryMode?: DeliveryMode
  /**
   * ÚNICO lugar que decide qué se limpia al cambiar de modo (vive en
   * RestaurantMicrosite; spec 2026-09-03 §4.2). El sheet conserva solo sus
   * efectos de navegación.
   */
  changeDeliveryMode: (m: DeliveryMode) => void
  dropoff?: DropoffAddress | null
  setDropoff?: (a: DropoffAddress | null) => void
  /** Cotización CONGELADA: `feeAmountCents` es la única verdad del envío (nunca recomputar). */
  heldQuote?: HeldQuote | null
  setHeldQuote?: (q: HeldQuote | null) => void
  // ─── Envío (Fase 1b T11) ────────────────────────────────────────────────────
  // Mismo motivo que delivery: vive en RestaurantMicrosite (sobrevive al cierre
  // del sheet).
  /** `shippingGateOpen()` + `location_shipping_config.enabled` + `features.shipping`, resuelto en `loadMicrositeData`. */
  shippingEnabled?: boolean
  /** ids de `items_shippable` para este local — I8: la vista es la única superficie del predicado, nunca reimplementarlo en TS. */
  shippableItemIds?: string[]
  shipTo?: ShipToAddress | null
  setShipTo?: (a: ShipToAddress | null) => void
  /** Cotización CONGELADA del envío: `amountCents` es la única verdad (nunca recomputar). */
  shipQuote?: ShipQuote | null
  setShipQuote?: (q: ShipQuote | null) => void
  /** Huella del carrito para el que se cotizó `shipQuote` (null = sin quote). El
   *  padre la guarda junto al quote para que sobreviva al desmontaje del sheet —
   *  ver `shipQuoteInvalidatedByCartChange`. */
  shipQuoteCartFingerprint?: string | null
  setShipQuoteCartFingerprint?: (fp: string | null) => void
  // ─── Cupón (spec §7.2) ────────────────────────────────────────────────────
  // Mismo motivo que delivery: el estado vive en RestaurantMicrosite porque
  // este sheet se DESMONTA al cerrarse, y el cliente que vuelve al menú por un
  // postre no debe perder el descuento que ya aplicó.
  /** GATE-COUPONS: false/undefined ⇒ el carrito ni dibuja el campo. */
  couponsEnabled?: boolean
  /** Checkout por pasos (spec 2026-10-19 §5). Apagado = el checkout de siempre. */
  checkoutStepsV2?: boolean
  /** Add-on de visitas (`features.visit_rewards`): el carrito ofrece canjear el premio. */
  visitRewardsEnabled?: boolean
  /** El cliente ya eligió recoger/domicilio en la barra de la carta. */
  fulfillmentChosenInBar?: boolean
  // ─── Sustituciones (spec sustituciones §4.2) ─────────────────────────────
  // El padre ya resolvió el flag crudo y el `!tableContext` (RestaurantMicrosite):
  // acá se recibe listo para usar. REQUERIDO a propósito — que ningún caller
  // futuro se olvide de decidirlo, ni siquiera en `false`.
  substitutionsEnabled: boolean
  /** Menú completo del local, para armar los candidatos de plan B por línea. */
  menuItems: MicrositeCartMenuItem[]
  /** % de la oferta de bienvenida por aceptar SMS de promociones; null = sin oferta. */
  welcomePct?: number | null
  appliedCoupon?: AppliedCoupon | null
  setAppliedCoupon?: (c: AppliedCoupon | null) => void
  /** Código de `?coupon=` todavía sin aplicar. */
  pendingCouponCode?: string | null
  /** Se llama tras el ÚNICO intento del deep link (salga bien o mal). */
  onPendingCouponConsumed?: () => void
  /** Reorder: datos del cliente ya conocidos. Siembra el formulario; el cliente puede editarlos. */
  initialCustomer?: Partial<CustomerInfo> | null
  /** Aviso a mostrar en el paso "cart" (p.ej. reorder con platos saltados, o prefill que falló). */
  notice?: string | null
  /** Task 11: true cuando el carrito nació de "Volver a pedir". Solo viaja hasta
   *  PaymentStep para telemetría (reorder_complete); nunca gobierna precio ni tenencia. */
  fromPrefill?: boolean
  /** Abre el explicador "¿Por qué cobramos envío?" (spec 2026-09-03). Solo delivery propio. */
  onWhyFee?: () => void
  /** Vuelta del pago en useredy.com («Dominio propio»): el sheet abre directo en
   *  la confirmación de esta orden. */
  initialConfirmedOrder?: ConfirmedOrder | null
  /** El pedido quedó creado y confirmado (id ya conocido). Telemetría de
   *  sugerencias (PR 5c): enlaza la sesión con el pedido. Best-effort. */
  onOrderConfirmed?: (orderId: string) => void
  /** Id de la sesión de sugerencias (PR 5c), para el stash del pago en otro host. */
  suggestionSessionId?: string | null
  /** Súper online (`online_retail_v2`, D10): al re-chequear el carrito, las líneas retail
   *  se reconstruyen al precio de hoy y se avisa qué cambió. Ausente/false = main. */
  retailV2?: boolean
  /** Con `retailV2`: datos frescos (precio, impuesto, 86) de los productos retail del
   *  carrito, para el carrito guardado (`usePersistedGroceryCart().updateFacts`). */
  onFreshFacts?: (facts: Record<string, Partial<CartLineFacts>>) => void
  /** Tienda restaurante | mercado (PR 2): sección de cada línea, solo en un local mixto con
   *  `online_retail_v2`. Se reenvía a `CartStep` como `lineSectionOf`. Ausente = main. */
  cartSectionOf?: (line: CartItem) => StorefrontSection
}

/** Snapshot de la orden confirmada que pinta el paso «confirm». */
export type ConfirmedOrder = {
  id: string
  number: string
  total: number
  scheduledFor: string | null
  receiptToken: string | null
  walletSaveUrl: string | null
  accountClaimUrl: string | null
  trackingUrl: string | null
  welcomeCoupon: WelcomeCoupon | null
  deliveryAddress: string | null
  deliveryEtaSeconds: number | null
  /** Hora en que el dispositivo vio el pedido confirmado (ISO): ancla la hora
   *  del día de la confirmación. Ausente (vuelta de pago alojado) = la del
   *  seguimiento. */
  placedAt?: string
  /** El tiempo de espera con el que pagó (PR K): la confirmación no cambia si la
   *  tienda se refresca. Ausente (vuelta de pago alojado) = el vigente. */
  waitEstimate?: WaitEstimate
  /** Preferencia de la ORDEN entera que devolvió /api/online-orders (o intent-status). */
  substitutionPref?: SubstitutionPref
  /** Avisos de degradación de la respuesta del servidor. */
  substitutionWarnings?: SubstitutionWarning[]
  /** Vuelta por «Dominio propio» (intent-status): `sms_opt_in` de la orden ya
   *  creada, que puede diferir del `contactAllowed` local (otro navegador/tab). */
  substitutionContactAllowed?: boolean
  /** Nombres de línea del carrito por índice, capturados ANTES de `setCart([])`
   *  — un aviso `cart_index >= 0` necesita el nombre de esa línea para pintarse
   *  y el carrito ya está vacío cuando ConfirmStep lo pinta. */
  confirmedCartNames?: string[]
  /** Valor de la compra para el pixel, calculado por el SERVIDOR (orders.subtotal). */
  conversion?: Conversion
}

export function OrderSheet({
  location,
  cart,
  setCart,
  pricingCfg,
  accentColor,
  pickupWaitMinutes,
  waitEstimate,
  confirmWaitEstimate,
  mapsUrl,
  acceptingOrders = true,
  openStatus,
  orderingPaused = false,
  pauseResumesAt = null,
  schedulingEnabled = false,
  scheduleDays = [],
  onClose,
  activeDonationCampaign,
  donationEnabled,
  tableContext,
  deliveryEnabled = false,
  deliveryMode = "pickup",
  changeDeliveryMode,
  dropoff = null,
  setDropoff,
  heldQuote = null,
  setHeldQuote,
  shippingEnabled = false,
  shippableItemIds = [],
  shipTo = null,
  setShipTo,
  shipQuote = null,
  setShipQuote,
  shipQuoteCartFingerprint = null,
  setShipQuoteCartFingerprint,
  couponsEnabled = false,
  checkoutStepsV2 = false,
  visitRewardsEnabled = false,
  fulfillmentChosenInBar = false,
  substitutionsEnabled,
  menuItems,
  welcomePct = null,
  appliedCoupon = null,
  setAppliedCoupon,
  pendingCouponCode = null,
  onPendingCouponConsumed,
  initialCustomer = null,
  notice = null,
  fromPrefill = false,
  onWhyFee,
  initialConfirmedOrder = null,
  onOrderConfirmed,
  suggestionSessionId,
  retailV2 = false,
  onFreshFacts,
  cartSectionOf,
}: Props) {
  const t = useTranslations('public.onlineOrder.checkout')
  // Idioma de la tienda: el peso de una línea del súper se escribe «1,5 lb» o «1.5 lb».
  const storeLocale = useLocale() === 'en' ? 'en' : 'es'
  const tCommonV2 = useTranslations('common')
  const [rawStep, setStep] = useState<Step>(initialConfirmedOrder ? "confirm" : "cart")
  const [donationAmount, setDonationAmount] = useState(0)
  // Propina al repartidor (spec 2026-09-12), en centavos. Solo vive en delivery.
  const [driverTipCents, setDriverTipCents] = useState(0)
  // Modo mesa (QR): el cliente elige comer en la mesa (default) o para llevar.
  const [qrOrderType, setQrOrderType] = useState<"dine_in" | "takeout">("dine_in")
  // Campos del modo mesa. `qrName` es OBLIGATORIO y se pide siempre; `qrPhone`
  // es opcional y se muestra en los dos submodos (el que come en la mesa
  // también puede levantarse).
  const [qrPhone, setQrPhone] = useState("")
  const [qrName, setQrName] = useState("")
  const donationActive = !!(donationEnabled && activeDonationCampaign)
  // Checkout por pasos (spec 2026-10-19 §5). En mesa por QR nunca: su flujo es otro.
  const v2 = checkoutStepsV2 && !tableContext
  // Delivery pedible acá: el local lo tiene encendido Y no es un pedido de mesa
  // por QR (un cliente sentado no pide a domicilio — buildSteps también lo excluye).
  const deliveryAvailable = deliveryEnabled && !tableContext
  const isDelivery = deliveryAvailable && deliveryMode === "delivery"
  // Al salir de domicilio la propina se borra: un pedido para recoger no tiene
  // repartidor, y dejarla viva la cobraría en un pedido pickup.
  useEffect(() => {
    if (!isDelivery && driverTipCents !== 0) setDriverTipCents(0)
  }, [isDelivery, driverTipCents])
  // Cotización viva CON margen de checkout: es lo que permite saltar el paso de
  // dirección. Un solo reloj (isQuoteExpired) — la barra usa el mismo.
  // Una cotización sin dirección nunca sirve para pagar: el setter del padre
  // puede resolver DESPUÉS de que "Recojo yo" ya limpió `dropoff`.
  const hasFreshQuote = isDelivery && !!dropoff && !!heldQuote && !isQuoteExpired(heldQuote, Date.now(), QUOTE_CHECKOUT_MARGIN_MS)
  // El paso de dirección sigue en `steps`; la navegación lo salta cuando ya hay
  // cotización viva. "Cambiar dirección" entra con setStep("address") explícito.
  const skipV1 = useMemo<ReadonlySet<Step>>(() => (hasFreshQuote ? new Set<Step>(['address']) : NO_SKIP), [hasFreshQuote])

  // Re-cotización silenciosa (spec §4.10). `inFlightRef` evita dos a la vez.
  const [requoting, setRequoting] = useState(false)
  const inFlightRef = useRef(false)

  /** Devuelve true si al terminar hay cotización viva para pagar. */
  async function requoteSilently(): Promise<boolean> {
    if (!dropoff) return false
    if (inFlightRef.current) return false
    inFlightRef.current = true
    setRequoting(true)
    try {
      const r = await requestDeliveryQuote(location.id, dropoff)
      if (r.kind === 'quoted') {
        setHeldQuote?.(r.quote)
        return true
      }
      // Fuera de zona o error: se tira la cotización y el cliente confirma a mano.
      setHeldQuote?.(null)
      setStep('address')
      return false
    } finally {
      inFlightRef.current = false
      setRequoting(false)
    }
  }

  // Re-verificación del carrito contra el menú FRESCO (lib/microsite/cartAvailability).
  // La carta se leyó al cargar la página y el carrito vive en el navegador: si algo
  // se agotó mientras el cliente armaba el pedido, se quita ANTES de cobrar y el
  // carrito dice qué fue. Es una ayuda, no la puerta: /session vuelve a validar, y
  // si el menú fresco no llega el carrito queda como está.
  const [soldOutNotice, setSoldOutNotice] = useState<SoldOutNotice | null>(null)
  // Súper online (D10): nombres de lo retail que cambió de precio. Siempre null sin la bandera.
  const [priceChangedNotice, setPriceChangedNotice] = useState<string[] | null>(null)
  const [checkingAvailability, setCheckingAvailability] = useState(false)
  // El carrito puede cambiar mientras la consulta está en vuelo: las líneas se
  // quitan del carrito VIGENTE, no del que había al preguntar.
  const cartRef = useRef(cart)
  useEffect(() => {
    cartRef.current = cart
  }, [cart])

  /** Devuelve true si el carrito quedó igual: nada que quitar, o sin menú fresco.
   *  La decisión es pura (`planCartRecheck`): sin `retailV2` es la de main, con las mismas
   *  llamadas (cartRecheckRestaurantRegression.test.ts). Con `retailV2` (D10) además
   *  reconstruye lo retail al precio de hoy sobre el MISMO menú pedido: sin otra consulta. */
  async function recheckCartAvailability(): Promise<boolean> {
    if (cartRef.current.length === 0) return true
    setCheckingAvailability(true)
    try {
      const menu = await requestCartAvailability(location.id, cartRef.current.map((l) => l.itemId))
      if (!menu) return true
      return applyCartRecheck(planCartRecheck(cartRef.current, menu, { retailV2, cfg: pricingCfg, locale: storeLocale }), {
        setCart,
        setSoldOutNotice,
        setPriceChangedNotice,
        onFreshFacts,
      })
    } finally {
      setCheckingAvailability(false)
    }
  }

  // Al ABRIR el checkout (el sheet se monta al abrirse): si la cotización no sirve
  // para pagar, se renueva sola con la misma dirección. Solo al montar, a propósito.
  // Y el carrito se contrasta con el menú fresco: lo que se agotó desde que el
  // cliente lo agregó sale antes de que llegue a pagar.
  useEffect(() => {
    if (shouldRequoteOnOpen({ deliveryMode: deliveryMode ?? 'pickup', dropoff: dropoff ?? null, heldQuote: heldQuote ?? null, now: Date.now() })) {
      void requoteSilently()
    }
    void recheckCartAvailability()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Envío (Fase 1b T11): a diferencia de `deliveryAvailable`, esto SÍ depende
  // del carrito (I12: un carrito mixto QSR+cajas no califica) — se recalcula en
  // cada render con el carrito vivo, no es un flag fijo del local.
  const shippableItemIdSet = useMemo(() => new Set(shippableItemIds), [shippableItemIds])
  const shippingAvailable = shipModeAvailable({
    shippingEnabled: !!shippingEnabled,
    tableContext: !!tableContext,
    cartItemIds: cart.map((i) => i.itemId),
    shippableItemIds: shippableItemIdSet,
  })
  const isShip = shippingAvailable && deliveryMode === "ship"
  // Secuencia de pasos derivada de los flags (ver stepMachine.ts). En delivery/ship
  // se inserta `address` entre el carrito y los datos del cliente.
  const hasFulfillmentChoice = deliveryAvailable || shippingAvailable
  const steps = useMemo(() => {
    // «Si algo no está disponible» como paso propio tras el carrito (owner,
    // 2026-11-03). Sin la bandera el objeto no gana ninguna clave: los flags que
    // reciben buildSteps/buildStepsV2 son exactamente los de main.
    const sub = substitutionsEnabled ? { substitutions: true } : {}
    return v2
      ? buildStepsV2({ tableContext: !!tableContext, donationActive, delivery: isDelivery, ship: isShip, hasFulfillmentChoice, ...sub })
      : buildSteps({ tableContext: !!tableContext, donationActive, delivery: isDelivery, ship: isShip, ...sub })
  }, [v2, tableContext, donationActive, isDelivery, isShip, hasFulfillmentChoice, substitutionsEnabled])
  // Paso EFECTIVO. Si el paso guardado desapareció de la secuencia (el caso real:
  // el cliente vuelve a pickup estando en `address`), ninguna rama renderizaría y
  // el sheet quedaría en blanco. Se degrada al carrito en el mismo render —
  // derivado, no un useEffect con setState (eso encadena renders).
  const step: Step = steps.includes(rawStep) ? rawStep : "cart"
  // Súper online (Tarea 2b.3): stock EN VIVO de las líneas retail, cada vez que se
  // entra al carrito. Sin líneas retail (todo restaurante) no hay ids y no se pide nada.
  const retailStockIds = useMemo(() => retailStockItemIds(cart, menuItems), [cart, menuItems])
  const retailStock = useRetailStock(location.id, retailStockIds, step === 'cart' && retailStockIds.length > 0)
  // Qué muestra el carrito en modo mesa. La regla vive en qrTableFields.ts, con
  // sus tests — acá solo se consume. Sin tableContext, `tableLabel` va undefined
  // y la función devuelve todo apagado (el checkout online normal).
  const fields = qrTableFields(tableContext?.tableLabel, qrOrderType)
  // Modo de pickup: ASAP por defecto; si está cerrado ahora pero se puede
  // agendar, arranca en "programar". scheduledFor es el ISO del slot elegido.
  const schedulable = schedulingEnabled && scheduleDays.length > 0
  const [scheduleMode, setScheduleMode] = useState<ScheduleMode>(
    !acceptingOrders && schedulable ? 'scheduled' : 'asap',
  )
  const [scheduledFor, setScheduledFor] = useState<string | null>(null)
  // Saltos de v2: ¿Cómo? si no hay qué elegir (o ya se eligió en la barra),
  // ¿Cuándo? si no hay horario que elegir, dirección con cotización viva.
  const needWhen = whenNeeded({ schedulable, isDelivery, isShip })
  const skip = useMemo<ReadonlySet<Step>>(
    () =>
      v2
        ? skipSetV2({
            hasFulfillmentChoice,
            fulfillmentChosenInBar,
            whenNeeded: needWhen,
            hasFreshQuote,
            deliveryBlocked: isDelivery && deliveryDisabledWhenClosed({ acceptingOrders }),
            shippingAvailable,
          })
        : skipV1,
    [v2, hasFulfillmentChoice, fulfillmentChosenInBar, needWhen, hasFreshQuote, skipV1, isDelivery, acceptingOrders, shippingAvailable],
  )
  // Rechazo del cupón AL INTENTAR PAGAR (spec §7.2/P1-18). `/session` valida el
  // cupón contra la fila viva ANTES de crear la sesión de pago: si entre el
  // carrito y el pago se agotó, venció o el carrito dejó de alcanzar el mínimo,
  // responde 422 y NO cobra. Este estado es lo que convierte ese 422 en algo que
  // el cliente entiende: se suelta el cupón, se vuelve al carrito con el total ya
  // corregido, y el aviso dice qué pasó. Nunca un error de pago genérico, y nunca
  // un cobro callado por el monto sin descuento.
  const [couponRejection, setCouponRejection] = useState<
    (SessionCouponRejection & { code: string }) | null
  >(null)
  // Premio de visitas (PR 5b). Un pedido lleva un solo cupón: aplicar el premio
  // quita el cupón y al revés (selectVisitReward / selectCoupon). El monto es vista
  // previa; /session recomputa y manda. Sin `visitRewardsEnabled` nada de esto se activa.
  const [appliedVisitReward, setAppliedVisitReward] = useState<AppliedVisitReward | null>(null)
  const [visitRewardNotice, setVisitRewardNotice] = useState<VisitRewardNotice | null>(null)
  const effectiveScheduledFor = scheduleMode === 'scheduled' ? scheduledFor : null
  const [customer, setCustomer] = useState<CustomerInfo>(() => ({
    name: initialCustomer?.name ?? '',
    phone: initialCustomer?.phone ?? '',
    email: initialCustomer?.email ?? '',
    pickupNote: '',
    billingZip: '',
    smsDecline: false,
    // Nunca marcada por defecto: una casilla pre-marcada no es consentimiento
    // expreso bajo TCPA y tira abajo el registro A2P del comercio.
    marketingOptIn: false,
  }))
  // Sustituciones (spec sustituciones §4.2): preferencia de la ORDEN entera
  // ("similar" por defecto — nunca se rechaza un pedido por esto). Vive acá
  // porque, igual que `customer`, PaymentStep la necesita en el POST y
  // ConfirmStep la necesita para pintar qué eligió el cliente.
  const [substitutionPref, setSubstitutionPref] = useState<SubstitutionPref>("similar")
  // "¿el cliente dejó un teléfono usable y no rechazó SMS?" — gobierna si
  // "contact" (avisarle por SMS) es una opción real en el selector.
  const contactAllowed = !!normalizePhone(customer.phone) && !customer.smsDecline
  // En el carrito todavía no hay teléfono: "Preguntarme primero" no se apaga por
  // eso (el teléfono se pide en «Tus datos»), solo si el cliente rechazó SMS.
  const contactPhonePending = !normalizePhone(customer.phone) && !customer.smsDecline
  // En modo mesa el cliente se deriva de la mesa en vez de pedirse en un
  // formulario.
  const effectiveCustomer = tableContext
    ? buildTableCustomer(tableContext.tableLabel, {
        // El nombre es obligatorio en modo mesa (el gate del carrito no deja
        // avanzar sin él), así que siempre viaja. El teléfono va en AMBOS modos:
        // el caso que motivó esto es el cliente de "En la mesa" que se levanta.
        // El servidor deriva el consentimiento SMS de que haya teléfono (#1401).
        name: qrName,
        phone: qrPhone,
      })
    : customer
  // Snapshot de la orden confirmada. Delivery incluido acá (y no leído del estado
  // vivo) porque la cotización se limpia al confirmar: la pantalla de éxito tiene
  // que seguir mostrando a dónde y en cuánto llega.
  const [confirmedOrder, setConfirmedOrder] = useState<ConfirmedOrder | null>(initialConfirmedOrder)

  // Fuera de useredy.com (dominio propio, redytoeat.com) North no monta, así que
  // el cobro se hace allá y el cliente vuelve aquí (lib/microsite/hostedPayment.ts).
  // Se decide por el host del navegador porque /order/[slug] es ISR: el mismo
  // HTML sirve a useredy.com, a redytoeat y al dominio del dueño.
  const [hostedPayment] = useState(
    () => typeof window !== "undefined" && shouldHandOffPayment(window.location.hostname),
  )

  // Lock body scroll while sheet is open (ref-counted para sheets apilados)
  useScrollLock()

  // If cart becomes empty mid-flow (before confirm), bounce to step 1
  useEffect(() => {
    if (cart.length === 0 && step !== "cart" && step !== "confirm") {
      setStep("cart")
    }
  }, [cart.length, step])

  // Precalentar el script de North mientras el cliente arma el carrito, para
  // que al tocar Pagar el iframe monte sin la espera de la descarga. No lleva
  // token ni monto: no hay riesgo de cobrar un carrito viejo.
  useEffect(() => {
    if (step !== 'cart' || cart.length === 0) return
    void loadCheckoutScript().catch(() => {
      // Sin ruido: PaymentStep vuelve a intentarlo y ahí sí reporta el error.
    })
  }, [step, cart.length])

  // Computed totals — price in CartItem is unit card price (no modifiers baked in).
  // PHASE F: when the merchant has processing_fee_enabled on, the per-tx flat fee
  // (default $0.20) is added as a separate processingFee line. Online orders are
  // always card-paid, so the fee always applies when the toggle is on.
  // ÚNICA fuente del envío en todo el sheet. Exige modo domicilio + cotización +
  // dirección: los tres son necesarios para que /session mande el quoteId, así que
  // atarlos acá garantiza POR CONSTRUCCIÓN que el envío que se MUESTRA es el mismo
  // que se COBRA (si divergieran, el cliente vería un total que no es el cargo).
  const activeQuote = isDelivery && dropoff ? heldQuote : null
  // Mismo criterio para envío (Fase 1b): modo ship + cotización + dirección
  // congeladas juntas. delivery y ship son mutuamente excluyentes (un solo
  // `deliveryMode`), así que solo uno de los dos puede estar vivo a la vez.
  const activeShipQuote = isShip && shipTo ? shipQuote : null
  // El envío se suma DESPUÉS del impuesto y NUNCA entra al subtotal: el servidor
  // hace `totalCents = round(subtotal + tax + processingFee)*100 + deliveryFeeCents`
  // (lib/online-orders/pricing). Meterlo al subtotal lo volvería gravable acá y no
  // allá → el total mostrado dejaría de ser el cobrado. Un solo número: CartStep y
  // PaymentStep no distinguen de dónde vino (delivery o ship), solo el rótulo cambia.
  const deliveryFee = activeQuote
    ? activeQuote.feeAmountCents / 100
    : activeShipQuote
      ? activeShipQuote.amountCents / 100
      : 0
  // G4 (espejo de /api/online-orders/session): sin `location.state` conocido, o
  // con el destino en otro estado, el envío NO cobra impuesto — fail-closed. Si
  // el cliente mandara `taxCents > 0` en ese caso el servidor rechaza con 422
  // `tax_mismatch`; calcularlo acá con la MISMA regla es lo que evita que pase.
  const effectiveTaxRate =
    isShip && shipTo && shipTaxMustBeZero(location.state ?? null, shipTo.state)
      ? 0
      : location.tax_rate ?? 0.0825
  // Subtotal de ítems SIN descuento: es el número contra el que el servidor mide
  // `min_subtotal` y sobre el que computa el porcentaje del cupón (spec §5.6).
  // DISPLAY: el precio BASE se muestra redondeado a tarjeta (online siempre es
  // tarjeta); los modificadores (price_delta) NUNCA se redondean. El cobro real
  // lo recalcula el servidor en /api/online-orders/session (autoritativo).
  // La expresión por línea vive en lib/microsite/couponUx (micrositeLineSubtotal)
  // para que el subtotal, la base del cupón y las `lines` del preview sean UNA.
  const subtotal = useMemo(() => micrositeCartSubtotal(cart, pricingCfg), [cart, pricingCfg])

  // A1b (TABC): base del cupón SIN las líneas de alcohol (listas del local que
  // guardó el cupón). Sin cupón o sin alcohol = el subtotal, intacto.
  const couponBase = useMemo(
    () => micrositeCouponBase(cart, pricingCfg, subtotal, appliedCoupon),
    [cart, pricingCfg, subtotal, appliedCoupon],
  )

  // Cupón: derivado del subtotal VIVO, no congelado en el momento de aplicarlo.
  // Por eso quitar un plato reajusta el descuento (y avisa si el carrito dejó de
  // alcanzar el mínimo) en el acto, en vez de mentir hasta el paso de pago.
  // A1b: el mínimo y el descuento se miden sobre la base sin alcohol, como /session.
  const couponState = couponDisplayState(appliedCoupon, subtotal, couponBase.couponBase, couponBase.excluded)
  // Premio de visitas: 100 % de UNA unidad elegible sin sus modificadores. Reemplaza al
  // cupón (nunca conviven: ver selectVisitReward). `null` = sin línea elegible: el
  // premio no baja nada ni viaja a /session.
  const visitRewardPreview = useMemo(
    () => (appliedVisitReward ? micrositeVisitRewardDiscount(cart, pricingCfg, appliedVisitReward) : null),
    [cart, pricingCfg, appliedVisitReward],
  )
  const visitRewardAmount = visitRewardPreview?.ok ? visitRewardPreview.amount : null
  const couponDiscount = visitRewardAmount ?? couponState.discount

  // La matemática vive en lib/microsite/couponUx (micrositeOrderTotals): es
  // dinero, y dentro de un useMemo de un componente este repo no puede probarla
  // (no hay jsdom). El orden —descuento, después impuesto— es el del servidor.
  const pricesIncludeTax = location.prices_include_tax === true
  // Súper online (I2): subtotal gravable solo si las filas traen `is_taxable` (con
  // online_retail_v2). Restaurante: undefined → la cuenta de main.
  const taxableSubtotal = useMemo(
    () => micrositeTaxableSubtotal(cart, menuItems, pricingCfg),
    [cart, menuItems, pricingCfg],
  )
  const { taxAmount, processingFee, total } = useMemo(
    () =>
      micrositeOrderTotals({
        subtotal,
        couponDiscount,
        taxRate: effectiveTaxRate,
        cashPerTxFee: location.cash_per_transaction_fee ?? 0,
        processingFeeEnabled: location.processing_fee_enabled ?? false,
        deliveryFee,
        driverTipCents: isDelivery ? driverTipCents : 0,
        pricesIncludeTax,
        taxableSubtotal,
      }),
    [subtotal, couponDiscount, effectiveTaxRate, location.cash_per_transaction_fee, location.processing_fee_enabled, deliveryFee, driverTipCents, isDelivery, pricesIncludeTax, taxableSubtotal],
  )

  // Cambio de modo pickup ⇄ delivery ⇄ ship. Al volver a pickup se BORRAN
  // dirección y cotización de LOS DOS modos: dejarlas vivas arriesga cobrar un
  // envío en un pedido para recoger. Al elegir domicilio/envío sin dirección se
  // salta directo al paso de dirección — es lo único que falta y el CTA del
  // carrito está bloqueado hasta cotizar.
  function handleChangeDeliveryMode(mode: DeliveryMode) {
    changeDeliveryMode(mode) // qué se limpia lo decide el padre
    // Sin programados a domicilio (2026-09-05): si venía en "programar", al
    // pasar a domicilio vuelve a ASAP — el picker desaparece en ese modo y un
    // slot escondido viajaría igual en el POST.
    if (mode === "delivery" && scheduleMode === "scheduled") {
      setScheduleMode("asap")
      setScheduledFor(null)
    }
    // v2: elegir la entrega es la respuesta del paso ¿Cómo?; el cliente avanza
    // con "Siguiente" (la dirección viene después de ¿Cuándo?).
    if (v2) return
    if (mode === "pickup") {
      setStep("cart")
    } else if (mode === "delivery" && !hasFreshQuote) {
      setStep("address")
    } else if (mode === "ship" && !shipQuote) {
      setStep("address")
    }
  }

  // El carrito cambió con una cotización de envío en mano ⇒ el fee ya no
  // corresponde al pedido (a diferencia de delivery, el fee de envío depende del
  // PESO/bultos del carrito). El servidor lo rechazaría igual por fingerprint
  // (I2), pero la UX no debe llegar ahí: se invalida ANTES, en el carrito.
  useEffect(() => {
    if (!shipQuote) return
    if (shipQuoteInvalidatedByCartChange(shipQuoteCartFingerprint, cart)) {
      setShipQuote?.(null)
      setShipQuoteCartFingerprint?.(null)
    }
  }, [cart, shipQuote, shipQuoteCartFingerprint, setShipQuote, setShipQuoteCartFingerprint])

  // El carrito dejó de calificar para envío (I12: se agregó un producto no
  // shippable, o se vació) mientras el modo elegido era "ship": se degrada a
  // pickup en el mismo render — mismo criterio que la degradación de `step`
  // más abajo, derivado, no un efecto con setState en cadena para el CASO
  // normal; acá SÍ hace falta useEffect porque también hay que limpiar
  // dirección/quote, no solo el paso.
  //
  // 🔴 CRITICAL (ronda de fix 1): la condición vive en `shippingCheckout.ts`
  // (`shipModeShouldDegradeToPickup`, con tests) — `step === "confirm"` NUNCA
  // degrada, mismo guard que el efecto de arriba (~253) que vacía el
  // carrito. `onSuccess` de PaymentStep hace `setCart([])` + `setStep("confirm")`
  // en el mismo batch — el carrito vacío hace que `shippingAvailable` calcule
  // false (cartItemIds vacío), y SIN este guard este efecto revertía el
  // `setStep("confirm")` de vuelta a "cart" (vía `handleChangeDeliveryMode("pickup")`):
  // el cliente que ACABA de pagar veía el carrito vacío en vez de su
  // confirmación — perdía el número de orden, el Wallet pass y el link del
  // recibo, sin haber tocado nada.
  useEffect(() => {
    if (shipModeShouldDegradeToPickup({ step, deliveryMode, shippingAvailable })) {
      handleChangeDeliveryMode("pickup")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryMode, shippingAvailable, step])

  // La cotización venció (o el local apagó delivery) a mitad del checkout: se tira
  // el quote y se devuelve al cliente al paso de dirección para cotizar de nuevo.
  // NUNCA se reintenta el mismo POST — cobraría un envío que ya no existe.
  function handleQuoteExpired() {
    setHeldQuote?.(null)
    setStep("address")
  }

  // Mismo criterio para el envío: /session lo rechazó (409/422 shipping_unavailable)
  // a mitad del checkout. Se tira el quote y se vuelve al paso de dirección —
  // reintentar el mismo POST cobraría un envío que ya no existe.
  function handleShipQuoteExpired() {
    setShipQuote?.(null)
    setShipQuoteCartFingerprint?.(null)
    setStep("address")
  }

  const cartStepProps: Omit<ComponentProps<typeof CartStep>, 'onContinue'> = {
    cart,
    setCart,
    pricingCfg,
    locale: storeLocale,
    subtotal,
    taxAmount,
    taxIncluded: pricesIncludeTax,
    processingFee,
    deliveryFee,
    total,
    accentColor,
    acceptingOrders,
    openStatus,
    orderingPaused,
    pauseResumesAt,
    substitutionsEnabled,
    menuItems,
    contactAllowed,
    contactPhonePending,
    schedulingEnabled,
    scheduleDays,
    scheduleMode,
    scheduledFor,
    onChangeMode: setScheduleMode,
    onChangeSlot: setScheduledFor,
    showTableName: fields.showName,
    tableName: qrName,
    onChangeTableName: setQrName,
    showQrPhone: fields.showQrPhone,
    qrPhone,
    onChangeQrPhone: setQrPhone,
    qrPhoneHelpKey: fields.qrPhoneHelpKey,
    tableNoticeLabel: fields.tableNoticeLabel,
    deliveryAvailable,
    deliveryMode,
    onChangeDeliveryMode: handleChangeDeliveryMode,
    deliveryAddressLine: dropoff && heldQuote ? formatDropoffLine(dropoff) : null,
    hasDeliveryQuote: !!heldQuote,
    pickupWaitMinutes: tableContext ? null : (pickupWaitMinutes ?? null),
    waitEstimate: tableContext ? undefined : waitEstimate,
    deliveryEtaSeconds: heldQuote?.etaDropoffSeconds ?? null,
    onEditAddress: () => setStep("address"),
    onWhyFee: isDelivery ? onWhyFee : undefined,
    shippingAvailable,
    shipAddressLine: shipTo && shipQuote ? formatDropoffLine(shipTo) : null,
    hasShipQuote: !!shipQuote,
    freeShippingApplied: !!activeShipQuote?.freeShippingApplied,
    couponsEnabled,
    locationId: location.id,
    appliedCoupon,
    couponDiscount,
    couponBelowMinimum: couponState.belowMinimum,
    couponMissingForMinimum: couponState.missingForMinimum,
    couponExcludedAlcohol: couponState.couponExcludedAlcohol === true,
    onApplyCoupon: (c) => {
                // Aplicar algo nuevo cierra el aviso del rechazo anterior: ya no
                // describe el estado de la pantalla.
                setCouponRejection(null)
                // Un pedido lleva un solo cupón: el cupón saca al premio (con aviso).
                const next = selectCoupon({ reward: appliedVisitReward }, c)
                setAppliedVisitReward(next.reward)
                setVisitRewardNotice(next.notice)
                setAppliedCoupon?.(c)
              },
    onRemoveCoupon: () => setAppliedCoupon?.(null),
    visitRewardsEnabled,
    appliedVisitReward,
    visitRewardDiscount: visitRewardAmount,
    visitRewardNotice,
    onApplyVisitReward: (reward: AppliedVisitReward) => {
      const next = selectVisitReward({ coupon: appliedCoupon }, reward)
      setCouponRejection(null)
      if (appliedCoupon) setAppliedCoupon?.(next.coupon)
      setAppliedVisitReward(next.reward)
      setVisitRewardNotice(next.notice)
    },
    onRemoveVisitReward: () => {
      setAppliedVisitReward(null)
      setVisitRewardNotice(null)
    },
    onDismissVisitRewardNotice: () => setVisitRewardNotice(null),
    couponRejection,
    onDismissCouponRejection: () => setCouponRejection(null),
    pendingCouponCode,
    onPendingCouponConsumed,
    soldOutNotice,
    onDismissSoldOutNotice: () => setSoldOutNotice(null),
    priceChangedNotice,
    onDismissPriceChangedNotice: () => setPriceChangedNotice(null),
    retailStock,
    continueBusy: requoting || checkingAvailability,
    onClose,
    // Restaurante | Mercado: solo si llegó (local mixto con la bandera). Sin ella el objeto no
    // gana ninguna clave: el CartStep de main.
    ...(cartSectionOf ? { lineSectionOf: cartSectionOf } : {}),
  }

  // v2: "Siguiente" desde Carrito / ¿Cómo? / ¿Cuándo?. Entre esos tres solo se
  // navega. Al salir del último hacia dirección/datos corre lo mismo que el
  // carrito viejo al avanzar: re-chequeo de agotados, begin_checkout y la
  // re-cotización silenciosa.
  async function continueV2(current: Step) {
    const target = nextStep(current, steps, skip)
    if (target === "substitutions" || target === "fulfillment" || target === "when") {
      setStep(target)
      return
    }
    if (!(await recheckCartAvailability())) {
      setStep("cart")
      return
    }
    setSoldOutNotice(null)
    // El aviso de precio describe el carrito; al salir de él ya no aplica (solo súper).
    if (retailV2) setPriceChangedNotice(null)
    const checkoutItems = trackItemsFromCart(cart, pricingCfg)
    track('begin_checkout', { value: cartValue(checkoutItems), currency: 'USD', items: checkoutItems })
    if (shouldRequoteOnOpen({ deliveryMode: deliveryMode ?? 'pickup', dropoff: dropoff ?? null, heldQuote: heldQuote ?? null, now: Date.now() })) {
      const ok = await requoteSilently()
      if (!ok) return // requoteSilently ya mandó al paso de dirección
      setStep(nextStep(current, steps, new Set<Step>([...skip, 'address'])))
      return
    }
    setStep(target)
  }

  // v2 · ¿Cuándo? con la tienda cerrada arranca en "programar": se preselecciona
  // el primer horario para que el cliente avance con un toque.
  useEffect(() => {
    if (!v2 || step !== "when" || scheduleMode !== "scheduled" || scheduledFor) return
    const first = scheduleDays[0]?.slots[0]?.iso
    if (first) setScheduledFor(first)
  }, [v2, step, scheduleMode, scheduledFor, scheduleDays])

  // Encabezado v2: pasos que el cliente realmente ve, total y desglose.
  const visibleSteps = steps.filter((x) => x !== "confirm" && (!skip.has(x) || x === step))
  const breakdown: BreakdownLine[] = [
    { label: tCommonV2('subtotal'), amount: subtotal },
    ...(couponDiscount > 0 && appliedCoupon ? [{ label: appliedCoupon.code, amount: -couponDiscount, accent: true }] : []),
    ...(visitRewardAmount != null && appliedVisitReward ? [{ label: appliedVisitReward.label, amount: -visitRewardAmount, accent: true }] : []),
    { label: tCommonV2(pricesIncludeTax ? 'taxIncluded' : 'tax'), amount: taxAmount },
    ...(processingFee > 0 ? [{ label: tCommonV2('processingFee'), amount: processingFee }] : []),
    ...(deliveryFee > 0 ? [{ label: t(isShip ? 'shippingFeeLabel' : 'deliveryFeeLabel'), amount: deliveryFee }] : []),
    ...(isDelivery && driverTipCents > 0 ? [{ label: t('progressTip'), amount: driverTipCents / 100 }] : []),
  ]
  const V2_TITLES: Partial<Record<Step, string>> = {
    cart: t('titleCart'),
    fulfillment: t('titleFulfillment'),
    when: t('titleWhen'),
  }

  // ─── Embudo del checkout (spec 2026-10-19 §5.9), en los DOS caminos ────────
  // Una sesión por apertura de la hoja. Abandono = la hoja se desmonta (o la
  // pestaña se va) sin pagar; `paid` y `handoff` lo apagan. Nunca bloquea nada.
  const [funnel] = useState<CheckoutFunnel>(() => {
    const f = makeCheckoutFunnel({ locationId: location.id, path: v2 ? 'v2' : 'v1' })
    // Vuelta del pago en dominio propio: la hoja abre directo en la confirmación.
    // Esa venta ya se midió con la sesión del stash; esta no es un checkout.
    if (initialConfirmedOrder) f.handoff()
    return f
  })
  const funnelCtx: FunnelCtx = {
    fulfillment: tableContext ? 'table' : (deliveryMode ?? 'pickup'),
    scheduled: effectiveScheduledFor != null,
  }
  // Lo cobrado = total + donación (mismo cálculo que PaymentStep), para que el
  // control "mostrado vs cobrado" no marque diferencias falsas con donación.
  const chargedTotal = total + donationCharge(donationAmount, 'card', effectiveCashDiscountPct(pricingCfg))
  const funnelLatest = useRef({ step: funnelStepOf(step), ctx: funnelCtx })
  funnelLatest.current = { step: funnelStepOf(step), ctx: funnelCtx }
  const prevFunnelStep = useRef<Step | null>(null)
  useEffect(() => {
    const prev = prevFunnelStep.current
    prevFunnelStep.current = step
    if (step === 'confirm') return
    // `substitutions` se mide como parte del carrito (funnelStepOf): entrar a él
    // no es "avanzar" ni otra vista; salir de él sí avanza el carrito.
    if (prev && funnelStepOf(prev) !== funnelStepOf(step) && steps.indexOf(step) > steps.indexOf(prev)) funnel.advanced(funnelStepOf(prev), funnelLatest.current.ctx)
    if (step !== 'substitutions') funnel.viewed(funnelStepOf(step), funnelLatest.current.ctx)
    // Solo cambia de paso: el contexto se lee del ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step])
  // El abandono por desmontaje se difiere un tick: StrictMode (dev) desmonta y
  // vuelve a montar al instante, y eso no es un cliente cerrando la hoja.
  const pendingLeave = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    if (pendingLeave.current) clearTimeout(pendingLeave.current)
    const leave = () => funnel.abandoned(funnelLatest.current.step, funnelLatest.current.ctx)
    window.addEventListener('pagehide', leave)
    return () => {
      window.removeEventListener('pagehide', leave)
      pendingLeave.current = setTimeout(leave, 0)
    }
  }, [funnel])

  // Step 4 has no back button — user can only close after confirmation
  function handleBackdropClick() {
    if (step === "confirm") return
    onClose()
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-50 bg-black/50 flex items-end justify-center"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-label="Checkout"
    >
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 32, stiffness: 320 }}
        onClick={(e) => e.stopPropagation()}
        // text-gray-900: la hoja vive DENTRO del wrapper del tema (color:
        // var(--ms-text) — marfil en dark luxe) y los inputs heredan color
        // (preflight), así que sin este reset lo tecleado sale marfil/blanco.
        className={v2
          // v2: una pregunta por pantalla ⇒ hoja a pantalla completa en teléfono
          // (spec 2026-10-19 §5.7); en sm+ igual que siempre.
          ? "relative w-full max-w-2xl bg-white text-gray-900 sm:rounded-t-3xl shadow-2xl flex flex-col h-[100dvh] sm:h-[90dvh]"
          : "relative w-full max-w-2xl bg-white text-gray-900 rounded-t-3xl shadow-2xl flex flex-col"}
        style={v2 ? undefined : { height: '90dvh' }}
      >
        {/* Drag handle */}
        <div className="flex-shrink-0 flex justify-center pt-2.5 pb-1">
          <div className="w-10 h-1.5 rounded-full bg-gray-300" />
        </div>
        {/* Modo mesa (QR): el cliente siempre ve a qué mesa va su orden y elige
            si es para comer aquí o para llevar (order_type → AQUÍ/LLEVAR en KDS). */}
        {tableContext && (
          <div className="flex-shrink-0 flex flex-col items-center gap-1.5 pb-2">
            <span className="text-xs font-bold px-3 py-1 rounded-full bg-gray-100 text-gray-700">
              {t('table', { table: tableContext.tableLabel })}
            </span>
            {step !== "confirm" && (
              /* Segmentado (no tarjetas como el de recojo/domicilio): esto es una
                 preferencia de servicio menor que vive en el chrome y persiste entre
                 pasos, no la decisión principal de la pantalla. Sí lleva ícono
                 dibujado y estado seleccionado claro — cero emoji. */
              <div className="inline-flex rounded-full bg-gray-100 p-[3px] gap-[3px]">
                {(
                  [
                    { key: "dine_in" as const, label: t('dineInHere'), Icon: UtensilsCrossed },
                    { key: "takeout" as const, label: t('takeout'), Icon: ShoppingBag },
                  ]
                ).map((opt) => {
                  const on = qrOrderType === opt.key
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => setQrOrderType(opt.key)}
                      aria-pressed={on}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${
                        on ? "bg-white text-gray-900 shadow-sm" : "text-gray-500"
                      }`}
                    >
                      <opt.Icon className="w-3.5 h-3.5" style={on ? { color: accentColor } : undefined} />
                      {opt.label}
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        )}

        <div className="flex-1 min-h-0 flex flex-col">
          {v2 && step !== "confirm" && (
            <CheckoutHeaderV2
              stepIndex={visibleSteps.indexOf(step) + 1}
              stepCount={visibleSteps.length}
              title={V2_TITLES[step] ?? ''}
              total={total}
              pendingDeliveryFee={isDelivery && !activeQuote}
              // Donación y Pago suman la donación al cobro (PaymentStep) y Pago trae
              // su propio resumen: ahí el encabezado no muestra un total que no es el cobrado.
              hideTotal={step === "donation" || step === "payment"}
              breakdown={breakdown}
              accentColor={accentColor}
              slim={!(step in V2_TITLES)}
              onBack={step === "cart" ? undefined : () => setStep(prevStep(step, steps, skip))}
              onClose={onClose}
            />
          )}
          {/* v2: el cuerpo del paso ocupa lo que deja el encabezado (si no, el
              h-full de cada paso empuja su botón fuera de la pantalla). */}
          <StepBody wrap={v2}>
          {!v2 && step !== "confirm" && (
            <StepProgress
              steps={steps}
              current={step}
              completed={hasFreshQuote ? new Set<Step>(["address"]) : NO_SKIP}
              accentColor={accentColor}
            />
          )}
          {step === "cart" && notice && (
            <div role="status" className="mx-4 mt-3 rounded-xl border px-4 py-3 text-sm" style={{ borderColor: 'var(--redy-border)', backgroundColor: 'var(--redy-surface)', color: 'var(--redy-charcoal)' }}>{notice}</div>
          )}
          {step === "cart" && !v2 && (
            <CartStep
              {...cartStepProps}
              onContinue={async () => {
                // Lo último antes de pedir datos y tarjeta: si algo se agotó, el
                // cliente se queda en el carrito con el aviso en vez de avanzar con
                // un pedido que /session le rechazaría en la pantalla de pago.
                if (!(await recheckCartAvailability())) return
                setSoldOutNotice(null)
                if (retailV2) setPriceChangedNotice(null)
                // Medición de anuncios (no-op sin pixel): iniciar el checkout es
                // SALIR del carrito hacia los datos, no solo abrir la hoja.
                const checkoutItems = trackItemsFromCart(cart, pricingCfg)
                track('begin_checkout', { value: cartValue(checkoutItems), currency: 'USD', items: checkoutItems })
                if (shouldRequoteOnOpen({ deliveryMode: deliveryMode ?? 'pickup', dropoff: dropoff ?? null, heldQuote: heldQuote ?? null, now: Date.now() })) {
                  const ok = await requoteSilently()
                  if (!ok) return // requoteSilently ya mandó al paso de dirección
                  // Con cotización nueva y viva, la dirección se salta.
                  setStep(nextStep('cart', steps, new Set<Step>(['address'])))
                  return
                }
                setStep(nextStep(step, steps, skip))
              }}
            />
          )}
          {v2 && (step === "cart" || step === "fulfillment" || step === "when") && (
            <CartStep
              {...cartStepProps}
              variant={step}
              gate={gateV2(step, {
                isEmpty: cart.length === 0,
                paused: orderingPaused === true,
                acceptingOrders,
                schedulable,
                isDelivery,
                scheduleMode,
                scheduledFor,
              })}
              deliveryDisabled={deliveryDisabledWhenClosed({ acceptingOrders })}
              onContinue={() => void continueV2(step)}
            />
          )}
          {step === "substitutions" && (
            <SubstitutionsStep
              value={substitutionPref}
              onChange={setSubstitutionPref}
              smsDeclined={customer.smsDecline}
              total={total}
              accentColor={accentColor}
              // v2: sigue dentro de la cabecera del checkout (¿Cómo? / ¿Cuándo?);
              // continueV2 corre el re-chequeo del carrito al salir de ella.
              onContinue={() => (v2 ? void continueV2(step) : setStep(nextStep(step, steps, skip)))}
              onBack={() => setStep(prevStep(step, steps, skip))}
            />
          )}
          {step === "address" && isShip && (
            <ShipAddressStep
              locationId={location.id}
              accentColor={accentColor}
              cart={cart}
              shipTo={shipTo}
              shipQuote={shipQuote}
              onQuote={(q, addr) => {
                setShipQuote?.(q)
                setShipTo?.(addr)
                setShipQuoteCartFingerprint?.(shipCartFingerprint(cart))
              }}
              onClearQuote={() => {
                setShipQuote?.(null)
                setShipQuoteCartFingerprint?.(null)
              }}
              onSwitchToPickup={() => { handleChangeDeliveryMode("pickup"); setStep("cart") }}
              onContinue={() => setStep(nextStep(step, steps, skip))}
              onBack={() => setStep(prevStep(step, steps, skip))}
            />
          )}
          {step === "address" && !isShip && (
            <AddressStep
              locationId={location.id}
              accentColor={accentColor}
              dropoff={dropoff}
              heldQuote={heldQuote}
              onQuote={(q, addr) => { setHeldQuote?.(q); setDropoff?.(addr) }}
              onClearQuote={() => setHeldQuote?.(null)}
              onSwitchToPickup={() => { handleChangeDeliveryMode("pickup"); setStep("cart") }}
              onContinue={() => setStep(nextStep(step, steps, skip))}
              onBack={() => setStep(prevStep(step, steps, skip))}
              onWhyFee={onWhyFee}
            />
          )}
          {step === "customer" && (
            <CustomerStep
              customer={customer}
              setCustomer={setCustomer}
              locationName={location.name}
              welcomePct={welcomePct}
              total={total}
              accentColor={accentColor}
              onContinue={() => setStep(nextStep(step, steps, skip))}
              onBack={() => setStep(prevStep(step, steps, skip))}
              {...(steps.includes('substitutions')
                ? {
                    substitutionPref,
                    onSubstitutionPrefChange: setSubstitutionPref,
                    onChangeSubstitution: () => setStep('substitutions'),
                  }
                : {})}
            />
          )}
          {step === "tip" && (
            <DriverTipStep
              accentColor={accentColor}
              initialTipCents={driverTipCents}
              onContinue={(cents) => { setDriverTipCents(cents); setStep(nextStep(step, steps, skip)) }}
              onBack={() => setStep(prevStep(step, steps, skip))}
            />
          )}
          {step === "donation" && activeDonationCampaign && (
            <DonationStep
              campaign={activeDonationCampaign}
              orderTotal={total}
              slug={location.slug}
              accentColor={accentColor}
              onContinue={(amt) => { setDonationAmount(amt); setStep(nextStep(step, steps, skip)) }}
              onBack={() => setStep(prevStep(step, steps, skip))}
            />
          )}
          {step === "payment" && (
            <PaymentStep
              locationId={location.id}
              slug={location.slug}
              cart={cart}
              customer={effectiveCustomer}
              subtotal={subtotal}
              taxAmount={taxAmount}
              taxIncluded={pricesIncludeTax}
              processingFee={processingFee}
              deliveryFee={deliveryFee}
              total={total}
              accentColor={accentColor}
              pricingCfg={pricingCfg}
              locale={storeLocale}
              scheduledFor={effectiveScheduledFor}
              donationAmount={donationAmount}
              donationCampaignId={activeDonationCampaign?.id ?? null}
              tableContext={tableContext}
              qrOrderType={tableContext ? qrOrderType : undefined}
              // Solo en domicilio: en pickup van null y el POST queda idéntico a hoy.
              // Mismo `activeQuote` que alimenta la línea "Envío" de los totales.
              deliveryQuote={activeQuote}
              driverTipCents={activeQuote ? driverTipCents : 0}
              dropoff={activeQuote ? dropoff : null}
              onDeliveryQuoteExpired={handleQuoteExpired}
              // Solo en envío: mismo `activeShipQuote` que alimenta la línea
              // "Envío" de los totales. Mutuamente excluyente con lo de arriba —
              // solo uno de los dos puede ser no-null (un solo `deliveryMode`).
              shipQuote={activeShipQuote}
              onShipQuoteExpired={handleShipQuoteExpired}
              // El código viaja SOLO si el cupón está descontando de verdad
              // (`sendToSession`). Con el carrito bajo el mínimo, mandarlo sería
              // pedir un 422 seguro en la pantalla de pago por algo que el
              // carrito ya sabía y ya explicó.
              couponCode={couponState.sendToSession ? (appliedCoupon?.code ?? null) : null}
              couponDiscount={couponDiscount}
              // Premio de visitas: solo viaja con una línea elegible (si no, el body
              // queda sin él y el pedido sigue normal). /session lo valida y acuña.
              visitRewardId={visitRewardAmount != null && appliedVisitReward ? appliedVisitReward.rewardId : null}
              visitRewardLabel={appliedVisitReward?.label ?? null}
              onVisitRewardRejected={(rejection) => {
                // Nadie pagó nada: se suelta el premio y se vuelve al carrito, donde el
                // aviso explica qué pasó y el cliente decide si sigue sin él.
                setAppliedVisitReward(null)
                setVisitRewardNotice(rejection)
                setStep("cart")
              }}
              substitutionsEnabled={substitutionsEnabled}
              substitutionPref={substitutionPref}
              onSubstitutionPrefChange={setSubstitutionPref}
              contactAllowed={contactAllowed}
              fromPrefill={fromPrefill}
              onHostedHandoff={hostedPayment ? (token) => {
                // Lo que la confirmación pinta tal cual lo vio el cliente (el
                // servidor no lo devuelve) + el carrito, por si cancela. Mismo
                // snapshot que arma onSuccess abajo.
                saveHandoffStash({
                  token,
                  slug: location.slug,
                  cart,
                  total,
                  scheduledFor: effectiveScheduledFor,
                  deliveryAddress: activeQuote && dropoff ? formatDropoffFull(dropoff) : null,
                  deliveryEtaSeconds: activeQuote?.etaDropoffSeconds ?? null,
                  expectWelcomeCoupon: welcomePct !== null && customer.marketingOptIn === true && !customer.smsDecline,
                  suggestionSessionId: suggestionSessionId ?? undefined,
                  funnel: funnel.sessionId
                    ? { sessionId: funnel.sessionId, path: v2 ? 'v2' : 'v1', ...funnelCtx, chargedTotal: chargedTotal }
                    : undefined,
                  savedAt: Date.now(),
                })
                funnel.handoff()
                window.location.assign(hostedPaymentUrl(location.slug, token, window.location.hostname))
              } : undefined}
              onCouponRejected={(rejection) => {
                // Nadie pagó nada: /session rechaza ANTES de crear la sesión de
                // pago. Se suelta el cupón (el total vuelve solo al valor real,
                // porque sale del subtotal vivo) y se devuelve al carrito, donde
                // el aviso explica qué pasó y el cliente decide si sigue sin él.
                const code = appliedCoupon?.code ?? ""
                setAppliedCoupon?.(null)
                setCouponRejection({ ...rejection, code })
                setStep("cart")
              }}
              onItemsUnavailable={(message) => {
                // Nadie pagó nada: /session rechaza ANTES de crear la sesión de pago.
                // Se vuelve al carrito y se re-mira el menú fresco para quitar lo que
                // ya no se vende. Si no llega, o ya no hay nada que quitar, el aviso
                // genérico igual explica por qué el cliente está de vuelta acá; con un
                // rechazo retail (R2: stock, peso, variante) el aviso es el mensaje del
                // servidor, que nombra el producto. Al volver al carrito el stock vivo
                // se pide de nuevo (useRetailStock) y las líneas dicen cuánto queda.
                setStep("cart")
                void recheckCartAvailability().then((igual) => {
                  if (igual) setSoldOutNotice(message ? { kind: 'message', text: message } : { kind: 'changed' })
                })
              }}
              onSuccess={(orderId, orderNumber, receiptToken, walletSaveUrl, accountClaimUrl, trackingUrl, welcomeCoupon, extras) => {
                setConfirmedOrder({
                  id: orderId,
                  number: orderNumber,
                  total,
                  scheduledFor: effectiveScheduledFor,
                  receiptToken,
                  walletSaveUrl,
                  accountClaimUrl,
                  trackingUrl,
                  welcomeCoupon,
                  deliveryAddress: activeQuote && dropoff ? formatDropoffFull(dropoff) : null,
                  deliveryEtaSeconds: activeQuote?.etaDropoffSeconds ?? null,
                  waitEstimate: confirmWaitEstimate ?? waitEstimate,
                  placedAt: new Date().toISOString(),
                  // I1: capturamos el valor ANTES del reset de abajo — si el
                  // servidor no devuelve substitutionPref (ruta vieja / best-effort
                  // caído), cae al closure `substitutionPref` de ESTE momento, no
                  // al valor ya reseteado a 'similar' que leería el render.
                  substitutionPref: extras?.substitutionPref ?? substitutionPref,
                  substitutionWarnings: extras?.substitutionWarnings,
                  // Antes de vaciar el carrito: un aviso de línea necesita el
                  // nombre de ESA línea y ConfirmStep lo pinta con el carrito ya vacío.
                  confirmedCartNames: cart.map((c) => c.name),
                  conversion: extras?.conversion,
                })
                try { onOrderConfirmed?.(orderId) } catch { /* telemetría: jamás afecta el pedido */ }
                funnel.paid({ ...funnelCtx, orderId, displayedTotal: chargedTotal })
                // Purchase del navegador ANTES de vaciar el carrito. eventID = id de
                // la orden (dedupe con la Conversions API); valor del servidor.
                trackPurchase({ orderId, conversion: extras?.conversion, items: trackItemsFromCart(cart, pricingCfg) })
                setCart([])
                setSubstitutionPref("similar")
                // La cotización ya se consumió: se limpia para que un segundo pedido
                // en la misma sesión cotice de nuevo en vez de arrastrar un quote
                // viejo. La dirección se conserva (le ahorra tipearla otra vez).
                setHeldQuote?.(null)
                setShipQuote?.(null)
                setShipQuoteCartFingerprint?.(null)
                setStep("confirm")
              }}
              onBack={() => setStep(prevStep(step, steps, skip))}
            />
          )}
          {step === "confirm" && confirmedOrder && (
            <ConfirmStep
              orderId={confirmedOrder.id}
              orderNumber={confirmedOrder.number}
              businessName={location.name}
              total={confirmedOrder.total}
              accentColor={accentColor}
              pickupWaitMinutes={tableContext ? undefined : pickupWaitMinutes}
              waitEstimate={tableContext ? undefined : (confirmedOrder.waitEstimate ?? confirmWaitEstimate ?? waitEstimate)}
              mapsUrl={tableContext ? undefined : mapsUrl}
              placedAt={confirmedOrder.placedAt ?? null}
              tableLabel={tableContext?.tableLabel}
              qrTakeout={tableContext ? qrOrderType === "takeout" : undefined}
              notifyPhone={tableContext && qrPhone.trim() ? qrPhone : null}
              scheduledFor={confirmedOrder.scheduledFor}
              timezone={location.timezone}
              deliveryAddress={confirmedOrder.deliveryAddress}
              deliveryEtaSeconds={confirmedOrder.deliveryEtaSeconds}
              receiptToken={confirmedOrder.receiptToken}
              walletSaveUrl={confirmedOrder.walletSaveUrl}
              accountClaimUrl={confirmedOrder.accountClaimUrl}
              trackingUrl={confirmedOrder.trackingUrl}
              welcomeCoupon={confirmedOrder.welcomeCoupon}
              // Si el cliente aceptó con oferta pero el cupón no llegó (el sondeo
              // de rescate le ganó al servidor), la confirmación vuelve a preguntar.
              expectWelcomeCoupon={welcomePct !== null && customer.marketingOptIn === true && !customer.smsDecline}
              substitutionsEnabled={substitutionsEnabled}
              substitutionPref={confirmedOrder.substitutionPref ?? substitutionPref}
              substitutionWarnings={confirmedOrder.substitutionWarnings ?? []}
              receiptCode={receiptCodeFromTrackingUrl(confirmedOrder.trackingUrl)}
              confirmedCartNames={confirmedOrder.confirmedCartNames ?? []}
              contactAllowed={contactAllowed || confirmedOrder.substitutionContactAllowed === true}
              onClose={onClose}
            />
          )}
          </StepBody>
        </div>
      </motion.div>
    </motion.div>
  )
}

/** v2 envuelve el cuerpo en un contenedor flexible; v1 queda sin wrapper (mismo DOM que main). */
function StepBody({ wrap, children }: { wrap: boolean; children: ReactNode }) {
  return wrap ? <div className="flex-1 min-h-0 flex flex-col">{children}</div> : <>{children}</>
}
